/**
 * The integration catalogue and the payloads it delivers.
 *
 * Everything here is pure so the shape of what leaves this product can be asserted in tests
 * rather than discovered in a customer's CRM. That matters more than usual: a mistake here
 * is not a crash, it is a contact silently created with a blank name, or an entire message
 * body written into a field that was meant to hold a reference.
 */

export type IntegrationId =
  | 'hubspot'
  | 'freshdesk'
  | 'zoho_crm'
  | 'zoho_desk'
  | 'google_sheets'
  | 'webhook'

/**
 * What triggers a delivery. Kept small and stable; these are a public contract.
 *
 * `task.created`/`task.completed` only fire for a task linked to a conversation — a
 * standalone task has no `contact`/`conversation` to populate, and those two fields are not
 * optional on `IntegrationEvent`, so a standalone task is a documented gap rather than a
 * payload with fabricated or missing required fields.
 */
/**
 * The outbound event registry — the one declarative table of what can leave the product as a
 * webhook or CRM delivery. Add a row and the union type, the human labels, and the generated
 * `docs/capabilities.json` all follow from it; `verify-registry.ts` fails the build if a literal
 * is emitted, subscribed to, or documented anywhere it is not also declared here. `category`
 * groups events for the manifest the MCP reads.
 */
export const WEBHOOK_EVENTS = {
  'message.received': { label: 'A customer messages you', category: 'message' },
  'message.updated': { label: 'A message is edited', category: 'message' },
  'message.deleted': { label: 'A message is deleted', category: 'message' },
  'message.ack.updated': { label: "A message's delivery status changes", category: 'message' },
  'message.flagged': { label: 'A message is flagged', category: 'message' },
  'message.unflagged': { label: 'A message is unflagged', category: 'message' },
  'chat.created': { label: 'A new chat starts', category: 'chat' },
  'chat.label_added': { label: 'A label is added to a chat', category: 'chat' },
  'chat.label_removed': { label: 'A label is removed from a chat', category: 'chat' },
  'reaction.created': { label: 'Someone reacts to a message', category: 'message' },
  'ticket.created': { label: 'A ticket is opened', category: 'ticket' },
  'ticket.updated': { label: 'A ticket is updated', category: 'ticket' },
  'ticket.resolved': { label: 'A ticket is resolved', category: 'ticket' },
  'ticket.deleted': { label: 'A ticket is deleted', category: 'ticket' },
  'ticket.label_added': { label: 'A label is added to a ticket', category: 'ticket' },
  'ticket.label_removed': { label: 'A label is removed from a ticket', category: 'ticket' },
  'task.created': { label: 'A task is created', category: 'task' },
  'task.completed': { label: 'A task is completed', category: 'task' },
} as const satisfies Record<string, { label: string; category: string }>

export type IntegrationEventType = keyof typeof WEBHOOK_EVENTS

/** Back-compat view over the registry: the subset the subscription UI renders. Derived, so it can
 *  never fall out of step with the union above. */
export const EVENT_LABELS = Object.fromEntries(
  Object.entries(WEBHOOK_EVENTS).map(([id, meta]) => [id, meta.label]),
) as Record<IntegrationEventType, string>

export interface CredentialField {
  key: string
  label: string
  hint?: string
  /** Secrets are write-only: stored encrypted and never sent back to the browser. */
  secret: boolean
  optional?: boolean
}

export interface IntegrationDefinition {
  id: IntegrationId
  name: string
  /** One line, shown on the card. Says what it does, not what it is. */
  summary: string
  /** Where the customer gets the credentials, so setup does not need a support ticket. */
  where: string
  credentials: CredentialField[]
  /** Settings that are configuration rather than secrets. */
  options: CredentialField[]
  events: IntegrationEventType[]
}

export const INTEGRATIONS: IntegrationDefinition[] = [
  {
    id: 'hubspot',
    name: 'HubSpot',
    summary: 'Creates or updates a HubSpot contact for anyone who messages you, and can send a WhatsApp message from a HubSpot workflow.',
    where: 'HubSpot → Settings → Integrations → Private Apps. The token needs crm.objects.contacts read and write.',
    credentials: [
      { key: 'accessToken', label: 'Private app token', secret: true, hint: 'Starts with pat-' },
      {
        key: 'workflowCallbackSecret',
        label: 'Workflow action callback secret',
        secret: true,
        optional: true,
        hint: 'Only needed if this app also registers the "Send a WhatsApp message" custom workflow action. Required for that callback to work at all: without it, the callback route refuses every request rather than trusting one with no secret behind it.',
      },
    ],
    options: [],
    events: ['message.received', 'ticket.created'],
  },
  {
    id: 'freshdesk',
    name: 'Freshdesk',
    summary: 'Opens a Freshdesk ticket whenever one is raised here.',
    where: 'Freshdesk → Profile settings → Your API Key.',
    credentials: [{ key: 'apiKey', label: 'API key', secret: true }],
    options: [
      {
        key: 'domain',
        label: 'Your Freshdesk domain',
        hint: 'The part before .freshdesk.com',
        secret: false,
      },
    ],
    events: ['ticket.created', 'ticket.resolved'],
  },
  {
    id: 'zoho_crm',
    name: 'Zoho CRM',
    summary: 'Adds a lead in Zoho CRM for each new WhatsApp contact, and can sync a Wami property back from a change in Zoho.',
    where: 'Zoho API Console → Self Client, then exchange the code for a refresh token.',
    credentials: [
      { key: 'refreshToken', label: 'Refresh token', secret: true },
      { key: 'clientId', label: 'Client ID', secret: false },
      { key: 'clientSecret', label: 'Client secret', secret: true },
      {
        key: 'notificationToken',
        label: 'Notification token',
        secret: true,
        optional: true,
        hint: 'A value you choose when registering Zoho\'s change-notification channel with a matching ?token= on the notify URL — proves a callback genuinely came from that channel.',
      },
    ],
    options: [
      {
        key: 'dataCenter',
        label: 'Data centre',
        hint: 'com, in, eu, com.au, jp, ca, sa, sg, ae',
        secret: false,
      },
      { key: 'module', label: 'Module', hint: 'Leads or Contacts', secret: false, optional: true },
      {
        key: 'skipTwoWaySync',
        label: 'Skip automatic two-way sync',
        hint: 'Set to "true" only on Zoho\'s free tier, which cannot emit the change notifications this relies on.',
        secret: false,
        optional: true,
      },
    ],
    events: ['message.received'],
  },
  {
    id: 'zoho_desk',
    name: 'Zoho Desk',
    summary: 'Mirrors tickets into Zoho Desk, with the conversation attached.',
    where: 'Zoho API Console → Self Client. You also need your Desk organisation ID.',
    credentials: [
      { key: 'refreshToken', label: 'Refresh token', secret: true },
      { key: 'clientId', label: 'Client ID', secret: false },
      { key: 'clientSecret', label: 'Client secret', secret: true },
    ],
    options: [
      { key: 'orgId', label: 'Organisation ID', secret: false },
      { key: 'departmentId', label: 'Department ID', secret: false, optional: true },
      { key: 'dataCenter', label: 'Data centre', hint: 'com, in, eu, …', secret: false },
    ],
    events: ['ticket.created', 'ticket.resolved'],
  },
  {
    id: 'google_sheets',
    name: 'Google Sheets',
    summary: 'Appends a row to a spreadsheet for every event you choose.',
    where: 'Google Cloud → Service account → Keys. Then share the sheet with that account’s email.',
    credentials: [
      { key: 'clientEmail', label: 'Service account email', secret: false },
      { key: 'privateKey', label: 'Private key', secret: true, hint: 'The full PEM block' },
    ],
    options: [
      { key: 'spreadsheetId', label: 'Spreadsheet ID', hint: 'The long id in the sheet URL', secret: false },
      { key: 'sheetName', label: 'Tab name', hint: 'Defaults to the first tab', secret: false, optional: true },
    ],
    events: ['message.received', 'ticket.created', 'ticket.resolved'],
  },
  {
    id: 'webhook',
    name: 'Zapier or a webhook',
    summary: 'Posts a signed JSON payload anywhere — Zapier, Make, or your own service.',
    where: 'In Zapier, create a Catch Hook trigger and paste its URL here.',
    credentials: [
      {
        key: 'signingSecret',
        label: 'Signing secret',
        secret: true,
        optional: true,
        hint: 'Optional. If set, each request carries an X-Wami-Signature you can verify.',
      },
    ],
    options: [{ key: 'url', label: 'Webhook URL', secret: false }],
    // Every registered event, not a hand-picked subset — the only provider that also carries
    // task and low-level chat events, so a fixed-field CRM adapter having no column for one is
    // no reason for the generic webhook to lack it too. Derived from WEBHOOK_EVENTS rather than
    // listed by hand: a hand-maintained list here silently stops delivering a new event the
    // moment it's added to the registry above but not copied down into this array — exactly the
    // gap that left `reaction.created`, `message.flagged`, and `message.unflagged` undeliverable
    // to every webhook subscriber for a full round of shipping before anyone noticed.
    events: Object.keys(WEBHOOK_EVENTS) as IntegrationEventType[],
  },
]

export function getIntegration(id: string): IntegrationDefinition | undefined {
  return INTEGRATIONS.find((entry) => entry.id === id)
}

/* ---------------------------------------------------------------- payloads */

/**
 * What every integration receives.
 *
 * One shape for all providers, mapped per adapter. A common envelope means a customer reading
 * their Zapier payload sees the same fields as one reading a spreadsheet row, and it keeps
 * the mapping logic in one testable place per provider instead of spread through the routes.
 */
export interface IntegrationEvent {
  id: string
  type: IntegrationEventType
  occurredAt: string
  workspaceId: string
  contact: {
    phone: string | null
    name: string | null
    /** WhatsApp identity, which is a group id for group chats. */
    remoteId: string
  }
  conversation: {
    id: string
    subject: string | null
    kind: 'direct' | 'group'
    channelNumber: string
  }
  message?: {
    id: string
    body: string
    sentAt: string
  }
  /**
   * On `message.received`/`chat.created`: the campaign send that most recently reached this
   * contact, so an outside orchestrator can tie a reply to the touch that drew it. Absent when
   * nothing was ever broadcast to them, or the last send was more than 30 days ago.
   */
  touch?: {
    broadcastId: string
    /** The caller's own label for the touch, when the send was started with one. */
    touchRef: string | null
    sentAt: string
  }
  /** `message.ack.updated` only — WhatsApp's own delivery-status transition. */
  ack?: {
    messageId: string
    status: 'sent' | 'delivered' | 'read' | 'failed'
  }
  ticket?: {
    id: string
    reference: string
    title: string
    category: string
    priority: 'low' | 'normal' | 'high' | 'urgent'
    status: string
    dueAt: string | null
  }
  task?: {
    id: string
    title: string
    status: string
    priority: 'low' | 'normal' | 'high' | 'urgent'
    assigneeUserId: string | null
    dueAt: string | null
  }
  /** `reaction.created` only. */
  reaction?: {
    emoji: string
    fromMe: boolean
    reactorPhone: string | null
  }
  /** `message.flagged` / `message.unflagged` only. */
  flag?: {
    messageId: string
    reason: string | null
  }
  /** `chat.label_added` / `chat.label_removed` / `ticket.label_added` / `ticket.label_removed`
   *  only — `ticket` is also set alongside this when the chat has an open ticket. */
  label?: {
    name: string
    added: boolean
  }
}

/**
 * Fields must never carry a whole conversation into a CRM's single-line text box, and a
 * provider that silently truncates leaves the customer with half a sentence and no warning.
 */
export function truncate(value: string, limit: number): string {
  if (value.length <= limit) return value
  return `${value.slice(0, Math.max(0, limit - 1)).trimEnd()}…`
}

/**
 * Splits a display name for systems that insist on two fields.
 *
 * A single-word name goes in the first name and leaves the last blank rather than being
 * duplicated — a contact called "Priya Priya" is worse than one with no surname.
 */
export function splitName(name: string | null): { first: string; last: string } {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { first: '', last: '' }
  if (parts.length === 1) return { first: parts[0], last: '' }
  return { first: parts[0], last: parts.slice(1).join(' ') }
}

/**
 * The API name Zoho gives a custom field created through its own UI for a label like "Order
 * number" — spaces become underscores, everything else is left exactly as typed. Used both to
 * write `Wami_Conversation_Id` and, in the two-way sync notification handler, to guess which
 * field on a changed Zoho record corresponds to which of Wami's own chat properties by name.
 */
export function zohoFieldNameFor(propertyName: string): string {
  return propertyName.trim().replace(/\s+/g, '_')
}

/**
 * Which of Wami's chat properties a changed Zoho record actually updates, matched by name —
 * pure so the matching logic can be asserted without a live account. A property whose Zoho
 * field is absent or unmapped is left alone rather than cleared, since an unset key in Zoho's
 * notification payload means "no change happened here," not "the value is now empty."
 */
export function mapZohoRecordToPropertyUpdates(
  record: Record<string, unknown>,
  chatProperties: { id: string; name: string }[],
): { defId: string; value: string }[] {
  const updates: { defId: string; value: string }[] = []
  for (const def of chatProperties) {
    const fieldName = zohoFieldNameFor(def.name)
    // `in` also matches inherited keys — a property named to slugify into `constructor` or
    // `toString` would otherwise read back an inherited function from Object.prototype and
    // "update" that property on every single notification, regardless of what Zoho actually
    // sent. `hasOwnProperty` only matches a key genuinely present on this parsed JSON object.
    if (!Object.prototype.hasOwnProperty.call(record, fieldName)) continue
    const value = record[fieldName]
    if (value === null || value === undefined) continue
    updates.push({ defId: def.id, value: String(value) })
  }
  return updates
}

/** A stable, readable one-liner used as a ticket subject or a row label. */
export function summarise(event: IntegrationEvent): string {
  const who = event.contact.name ?? event.contact.phone ?? 'Unknown contact'
  if (event.ticket) return `${event.ticket.reference} · ${event.ticket.title}`
  if (event.message) return `WhatsApp from ${who}: ${truncate(event.message.body, 60)}`
  return `WhatsApp from ${who}`
}

/** The row written to a spreadsheet. Column order is fixed — a sheet already has headers. */
export function toSheetRow(event: IntegrationEvent): string[] {
  return [
    event.occurredAt,
    event.type,
    event.contact.name ?? '',
    event.contact.phone ?? '',
    event.conversation.subject ?? '',
    event.conversation.channelNumber,
    event.message?.body ?? '',
    event.ticket?.reference ?? '',
    event.ticket?.category ?? '',
    event.ticket?.priority ?? '',
    event.ticket?.status ?? '',
  ]
}

export const SHEET_HEADERS = [
  'Time',
  'Event',
  'Contact',
  'Phone',
  'Subject',
  'Channel',
  'Message',
  'Ticket',
  'Category',
  'Priority',
  'Status',
]

/* ---------------------------------------------------------------- retries */

/** Give up after this many attempts and show the customer the error instead. */
export const MAX_ATTEMPTS = 6

/**
 * How long to wait before trying again.
 *
 * Exponential, capped at an hour, with jitter. The jitter is not decoration: every workspace
 * whose CRM went down at the same moment would otherwise retry in lockstep and arrive as a
 * thundering herd the instant it came back.
 */
export function backoffSeconds(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(3600, 30 * 2 ** Math.max(0, attempt - 1))
  return Math.round(base * (0.75 + random() * 0.5))
}

/** Whether a failed delivery is worth trying again. */
export function isRetryable(status: number): boolean {
  // 408 and 429 are explicitly "come back later"; 5xx is the provider's problem, not ours.
  if (status === 408 || status === 429) return true
  if (status >= 500) return true
  // A 4xx is a bad request or a rejected credential. Retrying cannot fix either, and doing
  // so for six hours only delays telling the customer their token has expired.
  return false
}
